<template>
  <div>
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>系统管理</el-breadcrumb-item>
      <el-breadcrumb-item>数据导入</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="box-card">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">数据导入</span>
      </div>

      <div class="upload-section">
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="upload-card">
              <h4>客户统计文件</h4>
              <el-upload
                ref="customerStatsUpload"
                class="upload-demo"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleCustomerStatsChange"
                :on-remove="handleCustomerStatsRemove"
                :before-upload="beforeUpload"
                :limit="1"
                :file-list="customerStatsFileList"
                accept=".xlsx,.xls"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                <div class="el-upload__tip" slot="tip">只能上传 xlsx/xls 文件，且不超过 10MB</div>
              </el-upload>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="upload-card">
              <h4>客户财务统计文件</h4>
              <el-upload
                ref="customerFinanceUpload"
                class="upload-demo"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleCustomerFinanceChange"
                :on-remove="handleCustomerFinanceRemove"
                :before-upload="beforeUpload"
                :limit="1"
                :file-list="customerFinanceFileList"
                accept=".xlsx,.xls"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                <div class="el-upload__tip" slot="tip">只能上传 xlsx/xls 文件，且不超过 10MB</div>
              </el-upload>
            </div>
          </el-col>
        </el-row>

        <div class="button-group">
          <el-button type="primary" @click="handleImport" :loading="loading" :disabled="!canImport">
            导入
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </div>

        <div v-if="canUpload || uploadStatus.uploading || uploadStatus.results.length > 0" class="upload-button-group">
          <el-button
            type="success"
            @click="handleUpload"
            :loading="uploadStatus.uploading && !uploadStatus.paused"
            :disabled="!canUpload"
          >
            {{ uploadStatus.uploading ? (uploadStatus.paused ? '继续上传' : '上传中...') : '数据上传' }}
          </el-button>

          <el-button
            v-if="uploadStatus.uploading && !uploadStatus.paused"
            @click="handlePause"
          >
            暂停
          </el-button>

          <el-button
            v-if="uploadStatus.uploading && uploadStatus.paused"
            @click="handleResume"
          >
            继续
          </el-button>

          <el-button
            v-if="uploadStatus.uploading"
            @click="handleCancel"
            type="danger"
          >
            取消
          </el-button>
        </div>
      </div>

      <div v-if="validationResults.headerCheck.errors.length > 0 ||
                validationResults.relationCheck.errors.length > 0 ||
                validationResults.dataCheck.errors.length > 0"
           class="validation-section">
        <el-divider>校验结果</el-divider>

        <el-steps :active="currentStep" finish-status="success" align-center>
          <el-step title="表头字段校验" :status="validationResults.headerCheck.passed ? 'success' : 'error'"></el-step>
          <el-step title="对应关系校验" :status="validationResults.relationCheck.passed ? 'success' : 'error'"></el-step>
          <el-step title="数据校验" :status="validationResults.dataCheck.passed ? 'success' : 'error'"></el-step>
        </el-steps>

        <div v-if="validationResults.headerCheck.errors.length > 0" class="error-section">
          <el-alert
            title="表头字段校验失败"
            type="error"
            :closable="false"
            show-icon
          >
            <div v-for="(error, index) in validationResults.headerCheck.errors" :key="'header-' + index">
              {{ error }}
            </div>
          </el-alert>
        </div>

        <div v-if="validationResults.relationCheck.errors.length > 0" class="error-section">
          <el-alert
            title="对应关系校验失败"
            type="error"
            :closable="false"
            show-icon
          >
            <div v-for="(error, index) in validationResults.relationCheck.errors" :key="'relation-' + index">
              {{ error }}
            </div>
          </el-alert>
        </div>

        <div v-if="validationResults.dataCheck.errors.length > 0" class="error-section">
          <el-alert
            title="数据校验失败"
            type="error"
            :closable="false"
            show-icon
          >
            <div v-for="(error, index) in validationResults.dataCheck.errors" :key="'data-' + index">
              {{ error }}
            </div>
          </el-alert>
        </div>
      </div>

      <div v-if="mergedData.length > 0" class="data-section">
        <el-divider>数据展示</el-divider>
        <el-table
          :data="mergedData"
          border
          stripe
          style="width: 100%"
          :height="500"
        >
          <el-table-column
            prop="customerName"
            label="客户名称"
            width="120"
            fixed
          ></el-table-column>
          <el-table-column
            prop="customerContact"
            label="客户联系人"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="customerPhone"
            label="客户电话"
            width="120"
          ></el-table-column>
          <el-table-column
            prop="customerSource"
            label="客户来源"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="employee"
            label="所属员工"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="signingTime"
            label="签约时间"
            width="120"
          ></el-table-column>
          <el-table-column
            prop="projectName"
            label="项目名称"
            width="120"
          ></el-table-column>
          <el-table-column
            prop="address"
            label="详细地址"
            width="200"
          ></el-table-column>
          <el-table-column
            prop="groupTime"
            label="拉群时间"
            width="120"
          ></el-table-column>
          <el-table-column
            prop="province"
            label="省份"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="city"
            label="城市"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="district"
            label="区县"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="lng"
            label="经度"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="lat"
            label="纬度"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="area"
            label="大区"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="signingType"
            label="签约类型"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="dudao"
            label="筹建督导"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="type"
            label="收支类型"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="feeType"
            label="收支分类"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="payType"
            label="钱款类型"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="payAccount"
            label="收款账号"
            width="150"
          ></el-table-column>
          <el-table-column
            prop="totalAmount"
            label="金额"
            width="100"
          ></el-table-column>
          <el-table-column
            prop="payDate"
            label="收款日期"
            width="120"
          ></el-table-column>
        </el-table>
      </div>

      <div v-if="uploadStatus.uploading || uploadStatus.results.length > 0" class="upload-progress-section">
        <el-divider>上传进度</el-divider>

        <el-progress
          :percentage="uploadProgress"
          :status="uploadProgressStatus"
          :stroke-width="20"
        >
          <span class="progress-text">
            {{ uploadStatus.currentIndex }} / {{ uploadStatus.total }}
            (成功: {{ uploadStatus.successCount }}, 失败: {{ uploadStatus.failCount }})
          </span>
        </el-progress>
      </div>

      <div v-if="uploadStatus.results.length > 0" class="upload-results-section">
        <el-divider>上传结果</el-divider>

        <el-table
          :data="uploadStatus.results"
          border
          stripe
          :height="400"
        >
          <el-table-column
            prop="index"
            label="序号"
            width="80"
            fixed
          ></el-table-column>

          <el-table-column
            prop="customerName"
            label="客户名称"
            width="150"
            fixed
          ></el-table-column>

          <el-table-column
            prop="status"
            label="状态"
            width="100"
          >
            <template slot-scope="scope">
              <el-tag
                :type="getStatusType(scope.row.status)"
                effect="dark"
              >
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            prop="message"
            label="结果消息"
            width="150"
          ></el-table-column>

          <el-table-column
            prop="details"
            label="详细结果"
            min-width="200"
          >
            <template slot-scope="scope">
              <div v-if="scope.row.details && scope.row.details.length > 0">
                <div v-for="(detail, index) in scope.row.details" :key="index">
                  {{ detail }}
                </div>
              </div>
              <div v-else-if="scope.row.error" style="color: #f56c6c;">
                {{ scope.row.error }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="timestamp"
            label="上传时间"
            width="180"
          ></el-table-column>

          <el-table-column
            label="操作"
            width="100"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.status === 'fail'"
                type="text"
                size="small"
                @click="handleRetry(scope.row.index)"
              >
                重试
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div v-if="uploadStatus.results.length > 0 && !uploadStatus.uploading" class="upload-summary-section">
        <el-alert
          :title="`上传完成！共 ${uploadStatus.total} 条数据，成功 ${uploadStatus.successCount} 条，失败 ${uploadStatus.failCount} 条`"
          :type="uploadStatus.failCount === 0 ? 'success' : 'warning'"
          :closable="false"
          show-icon
        >
          <div v-if="uploadStatus.failCount > 0">
            <el-button type="text" @click="handleRetryAllFailed">重试失败项</el-button>
          </div>
        </el-alert>
      </div>
    </el-card>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'

const CUSTOMER_STATS_HEADERS = [
  '客户名称', '客户联系人', '客户电话', '客户来源', '所属员工',
  '签约时间', '项目名称', '详细地址', '拉群时间', '省份', '城市',
  '区县', '经度', '纬度', '大区', '签约类型', '筹建督导'
]

const CUSTOMER_FINANCE_HEADERS = [
  '客户名称', '项目名称', '收支类型', '收支分类', '钱款类型',
  '收款账号', '金额', '收款日期'
]

const FIELD_MAPPING = {
  客户名称: 'customerName',
  客户联系人: 'customerContact',
  客户电话: 'customerPhone',
  客户来源: 'customerSource',
  所属员工: 'employee',
  签约时间: 'signingTime',
  项目名称: 'projectName',
  详细地址: 'address',
  拉群时间: 'groupTime',
  省份: 'province',
  城市: 'city',
  区县: 'district',
  经度: 'lng',
  纬度: 'lat',
  大区: 'area',
  签约类型: 'signingType',
  筹建督导: 'dudao',
  收支类型: 'type',
  收支分类: 'feeType',
  钱款类型: 'payType',
  收款账号: 'payAccount',
  金额: 'totalAmount',
  收款日期: 'payDate'
}

export default {
  data () {
    return {
      customerStatsFile: null,
      customerFinanceFile: null,
      customerStatsFileList: [],
      customerFinanceFileList: [],
      customerStatsData: [],
      customerFinanceData: [],
      mergedData: [],
      validationResults: {
        headerCheck: { passed: false, errors: [] },
        relationCheck: { passed: false, errors: [] },
        dataCheck: { passed: false, errors: [] }
      },
      currentStep: 0,
      loading: false,
      uploadStatus: {
        uploading: false,
        paused: false,
        cancelled: false,
        currentIndex: 0,
        total: 0,
        successCount: 0,
        failCount: 0,
        results: []
      }
    }
  },
  computed: {
    canImport () {
      return this.customerStatsFile && this.customerFinanceFile
    },
    canUpload () {
      return this.mergedData.length > 0 &&
             this.validationResults.headerCheck.passed &&
             this.validationResults.relationCheck.passed &&
             this.validationResults.dataCheck.passed &&
             !this.uploadStatus.uploading
    },
    uploadProgress () {
      if (this.uploadStatus.total === 0) return 0
      return Math.round((this.uploadStatus.currentIndex / this.uploadStatus.total) * 100)
    },
    uploadProgressStatus () {
      if (this.uploadStatus.failCount > 0) return 'exception'
      if (this.uploadStatus.currentIndex === this.uploadStatus.total) return 'success'
      return undefined
    }
  },
  methods: {
    beforeUpload (file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel'
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isExcel) {
        this.$message.error('只能上传 Excel 文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },

    handleCustomerStatsChange (file, fileList) {
      this.customerStatsFile = file.raw
      this.customerStatsFileList = fileList
    },

    handleCustomerStatsRemove () {
      this.customerStatsFile = null
      this.customerStatsFileList = []
    },

    handleCustomerFinanceChange (file, fileList) {
      this.customerFinanceFile = file.raw
      this.customerFinanceFileList = fileList
    },

    handleCustomerFinanceRemove () {
      this.customerFinanceFile = null
      this.customerFinanceFileList = []
    },

    async handleImport () {
      this.loading = true
      this.validationResults = {
        headerCheck: { passed: false, errors: [] },
        relationCheck: { passed: false, errors: [] },
        dataCheck: { passed: false, errors: [] }
      }
      this.currentStep = 0
      this.mergedData = []

      try {
        await this.parseExcelFiles()
        await this.validateHeaders()
        if (!this.validationResults.headerCheck.passed) {
          this.loading = false
          return
        }

        this.currentStep = 1
        await this.validateRelations()
        if (!this.validationResults.relationCheck.passed) {
          this.loading = false
          return
        }

        this.currentStep = 2
        await this.validateData()
        if (!this.validationResults.dataCheck.passed) {
          this.loading = false
          return
        }

        await this.mergeData()
        this.$message.success('数据导入成功!')
      } catch (error) {
        this.$message.error('导入失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    parseExcelFiles () {
      return new Promise((resolve, reject) => {
        try {
          const reader1 = new FileReader()
          const reader2 = new FileReader()

          reader1.onload = (e) => {
            try {
              const data = new Uint8Array(e.target.result)
              const workbook = XLSX.read(data, { type: 'array' })
              const firstSheetName = workbook.SheetNames[0]
              const worksheet = workbook.Sheets[firstSheetName]
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd', header: 1 })

              if (jsonData.length < 2) {
                reject(new Error('客户统计文件数据为空'))
                return
              }

              const headers = jsonData[0].map(h => typeof h === 'string' ? h.trim() : h)
              const rows = jsonData.slice(1).map(row => {
                const obj = {}
                headers.forEach((header, index) => {
                  const value = row[index]
                  obj[header] = typeof value === 'string' ? value.trim() : value
                })
                return obj
              })

              this.customerStatsData = { headers, rows }

              reader2.onload = (e2) => {
                try {
                  const data2 = new Uint8Array(e2.target.result)
                  const workbook2 = XLSX.read(data2, { type: 'array' })
                  const firstSheetName2 = workbook2.SheetNames[0]
                  const worksheet2 = workbook2.Sheets[firstSheetName2]
                  const jsonData2 = XLSX.utils.sheet_to_json(worksheet2, { raw: false, dateNF: 'yyyy-mm-dd', header: 1 })

                  if (jsonData2.length < 2) {
                    reject(new Error('客户财务统计文件数据为空'))
                    return
                  }

                  const headers2 = jsonData2[0].map(h => typeof h === 'string' ? h.trim() : h)
                  const rows2 = jsonData2.slice(1).map(row => {
                    const obj = {}
                    headers2.forEach((header, index) => {
                      const value = row[index]
                      obj[header] = typeof value === 'string' ? value.trim() : value
                    })
                    return obj
                  })

                  this.customerFinanceData = { headers: headers2, rows: rows2 }
                  resolve()
                } catch (error) {
                  reject(new Error('解析客户财务统计文件失败: ' + error.message))
                }
              }

              reader2.readAsArrayBuffer(this.customerFinanceFile)
            } catch (error) {
              reject(new Error('解析客户统计文件失败: ' + error.message))
            }
          }

          reader1.readAsArrayBuffer(this.customerStatsFile)
        } catch (error) {
          reject(error)
        }
      })
    },

    validateHeaders () {
      return new Promise((resolve) => {
        const errors = []

        const missingStatsHeaders = CUSTOMER_STATS_HEADERS.filter(
          header => !this.customerStatsData.headers.includes(header)
        )

        if (missingStatsHeaders.length > 0) {
          errors.push(`客户统计文件缺少以下字段: ${missingStatsHeaders.join(', ')}`)
        }

        const missingFinanceHeaders = CUSTOMER_FINANCE_HEADERS.filter(
          header => !this.customerFinanceData.headers.includes(header)
        )

        if (missingFinanceHeaders.length > 0) {
          errors.push(`客户财务统计文件缺少以下字段: ${missingFinanceHeaders.join(', ')}`)
        }

        this.validationResults.headerCheck = {
          passed: errors.length === 0,
          errors
        }

        resolve()
      })
    },

    validateRelations () {
      return new Promise((resolve) => {
        const errors = []

        if (this.customerStatsData.rows.length !== this.customerFinanceData.rows.length) {
          errors.push(`两个文件的数据行数不一致: 客户统计文件${this.customerStatsData.rows.length}行, 客户财务统计文件${this.customerFinanceData.rows.length}行`)
        }

        const minRows = Math.min(this.customerStatsData.rows.length, this.customerFinanceData.rows.length)

        for (let i = 0; i < minRows; i++) {
          const row1 = this.customerStatsData.rows[i]
          const row2 = this.customerFinanceData.rows[i]

          const customerName1 = typeof row1['客户名称'] === 'string' ? row1['客户名称'].trim() : row1['客户名称']
          const projectName1 = typeof row1['项目名称'] === 'string' ? row1['项目名称'].trim() : row1['项目名称']
          const customerName2 = typeof row2['客户名称'] === 'string' ? row2['客户名称'].trim() : row2['客户名称']
          const projectName2 = typeof row2['项目名称'] === 'string' ? row2['项目名称'].trim() : row2['项目名称']

          if (customerName1 !== customerName2 || projectName1 !== projectName2) {
            errors.push(`第${i + 1}行数据不匹配: 客户统计[${customerName1}-${projectName1}] vs 客户财务[${customerName2}-${projectName2}]`)
          }
        }

        this.validationResults.relationCheck = {
          passed: errors.length === 0,
          errors
        }

        resolve()
      })
    },

    validateDate (dateStr) {
      if (!dateStr) return true
      const patterns = [
        /^\d{1,2}\/\d{1,2}\/\d{2}$/,
        /^\d{4}\/\d{1,2}\/\d{1,2}$/
      ]
      return patterns.some(pattern => pattern.test(dateStr))
    },

    validateData () {
      return new Promise((resolve) => {
        const errors = []

        this.customerStatsData.rows.forEach((row, index) => {
          const rowNum = index + 1

          const customerName = typeof row['客户名称'] === 'string' ? row['客户名称'].trim() : row['客户名称']
          const customerContact = typeof row['客户联系人'] === 'string' ? row['客户联系人'].trim() : row['客户联系人']
          const customerPhone = typeof row['客户电话'] === 'string' ? row['客户电话'].trim() : row['客户电话']
          const customerSource = typeof row['客户来源'] === 'string' ? row['客户来源'].trim() : row['客户来源']
          const employee = typeof row['所属员工'] === 'string' ? row['所属员工'].trim() : row['所属员工']
          const signingTime = typeof row['签约时间'] === 'string' ? row['签约时间'].trim() : row['签约时间']
          const projectName = typeof row['项目名称'] === 'string' ? row['项目名称'].trim() : row['项目名称']
          const address = typeof row['详细地址'] === 'string' ? row['详细地址'].trim() : row['详细地址']
          const groupTime = typeof row['拉群时间'] === 'string' ? row['拉群时间'].trim() : row['拉群时间']
          const province = typeof row['省份'] === 'string' ? row['省份'].trim() : row['省份']
          const city = typeof row['城市'] === 'string' ? row['城市'].trim() : row['城市']
          const lng = typeof row['经度'] === 'string' ? row['经度'].trim() : row['经度']
          const lat = typeof row['纬度'] === 'string' ? row['纬度'].trim() : row['纬度']
          const area = typeof row['大区'] === 'string' ? row['大区'].trim() : row['大区']
          const signingType = typeof row['签约类型'] === 'string' ? row['签约类型'].trim() : row['签约类型']

          if (!customerName) errors.push(`客户统计第${rowNum}行: 客户名称不能为空`)
          if (!customerContact) errors.push(`客户统计第${rowNum}行: 客户联系人不能为空`)
          if (!customerPhone) errors.push(`客户统计第${rowNum}行: 客户电话不能为空`)
          if (!customerSource) errors.push(`客户统计第${rowNum}行: 客户来源不能为空`)
          if (!employee) errors.push(`客户统计第${rowNum}行: 所属员工不能为空`)

          if (!signingTime) {
            errors.push(`客户统计第${rowNum}行: 签约时间不能为空`)
          } else if (!this.validateDate(signingTime)) {
            errors.push(`客户统计第${rowNum}行: 签约时间格式错误，应为 m/d/yy 或 yyyy/m/d, 实际值: ${signingTime}`)
          }

          if (!projectName) errors.push(`客户统计第${rowNum}行: 项目名称不能为空`)
          if (!address) errors.push(`客户统计第${rowNum}行: 详细地址不能为空`)

          if (groupTime && !this.validateDate(groupTime)) {
            errors.push(`客户统计第${rowNum}行: 拉群时间格式错误，应为 m/d/yy 或 yyyy/m/d, 实际值: ${groupTime}`)
          }

          if (!province) errors.push(`客户统计第${rowNum}行: 省份不能为空`)
          if (!city) errors.push(`客户统计第${rowNum}行: 城市不能为空`)
          if (!lng) errors.push(`客户统计第${rowNum}行: 经度不能为空`)
          if (!lat) errors.push(`客户统计第${rowNum}行: 纬度不能为空`)
          if (!area) errors.push(`客户统计第${rowNum}行: 大区不能为空`)
          if (!signingType) errors.push(`客户统计第${rowNum}行: 签约类型不能为空`)
        })

        this.customerFinanceData.rows.forEach((row, index) => {
          const rowNum = index + 1

          const customerName = typeof row['客户名称'] === 'string' ? row['客户名称'].trim() : row['客户名称']
          const projectName = typeof row['项目名称'] === 'string' ? row['项目名称'].trim() : row['项目名称']
          const type = typeof row['收支类型'] === 'string' ? row['收支类型'].trim() : row['收支类型']
          const feeType = typeof row['收支分类'] === 'string' ? row['收支分类'].trim() : row['收支分类']
          const payType = typeof row['钱款类型'] === 'string' ? row['钱款类型'].trim() : row['钱款类型']
          const payAccount = typeof row['收款账号'] === 'string' ? row['收款账号'].trim() : row['收款账号']
          const totalAmount = typeof row['金额'] === 'string' ? row['金额'].trim() : row['金额']
          const payDate = typeof row['收款日期'] === 'string' ? row['收款日期'].trim() : row['收款日期']

          if (!customerName) errors.push(`客户财务统计第${rowNum}行: 客户名称不能为空`)
          if (!projectName) errors.push(`客户财务统计第${rowNum}行: 项目名称不能为空`)

          if (!type) {
            errors.push(`客户财务统计第${rowNum}行: 收支类型不能为空`)
          } else if (type !== '收款' && type !== '退款') {
            errors.push(`客户财务统计第${rowNum}行: 收支类型必须为"收款"或"退款"`)
          }

          if (!feeType) errors.push(`客户财务统计第${rowNum}行: 收支分类不能为空`)
          if (!payType) errors.push(`客户财务统计第${rowNum}行: 钱款类型不能为空`)
          if (!payAccount) errors.push(`客户财务统计第${rowNum}行: 收款账号不能为空`)
          if (!totalAmount) errors.push(`客户财务统计第${rowNum}行: 金额不能为空`)

          if (!payDate) {
            errors.push(`客户财务统计第${rowNum}行: 收款日期不能为空`)
          } else if (!this.validateDate(payDate)) {
            errors.push(`客户财务统计第${rowNum}行: 收款日期格式错误，应为 m/d/yy 或 yyyy/m/d, 实际值: ${payDate}`)
          }
        })

        this.validationResults.dataCheck = {
          passed: errors.length === 0,
          errors
        }

        resolve()
      })
    },

    mergeData () {
      return new Promise((resolve) => {
        this.mergedData = this.customerStatsData.rows.map((row, index) => {
          const financeRow = this.customerFinanceData.rows[index]
          const merged = {}

          Object.keys(FIELD_MAPPING).forEach(chineseKey => {
            const englishKey = FIELD_MAPPING[chineseKey]
            if (row[chineseKey] !== undefined) {
              merged[englishKey] = row[chineseKey]
            } else if (financeRow[chineseKey] !== undefined) {
              merged[englishKey] = financeRow[chineseKey]
            }
          })

          return merged
        })

        resolve()
      })
    },

    handleReset () {
      this.customerStatsFile = null
      this.customerFinanceFile = null
      this.customerStatsFileList = []
      this.customerFinanceFileList = []
      this.customerStatsData = []
      this.customerFinanceData = []
      this.mergedData = []
      this.validationResults = {
        headerCheck: { passed: false, errors: [] },
        relationCheck: { passed: false, errors: [] },
        dataCheck: { passed: false, errors: [] }
      }
      this.currentStep = 0
      this.loading = false
      this.uploadStatus = {
        uploading: false,
        paused: false,
        cancelled: false,
        currentIndex: 0,
        total: 0,
        successCount: 0,
        failCount: 0,
        results: []
      }
      this.$refs.customerStatsUpload.clearFiles()
      this.$refs.customerFinanceUpload.clearFiles()
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      const patterns = [
        /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,
        /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/
      ]
      for (const pattern of patterns) {
        const match = dateStr.match(pattern)
        if (match) {
          let year, month, day
          if (match[1].length === 4) {
            year = match[1]
            month = match[2].padStart(2, '0')
            day = match[3].padStart(2, '0')
          } else {
            year = '20' + match[3]
            month = match[1].padStart(2, '0')
            day = match[2].padStart(2, '0')
          }
          return `${year}-${month}-${day}`
        }
      }
      return dateStr
    },

    prepareUploadData (data) {
      return {
        customerName: typeof data.customerName === 'string' ? data.customerName.trim() : data.customerName,
        customerContact: typeof data.customerContact === 'string' ? data.customerContact.trim() : data.customerContact,
        customerPhone: typeof data.customerPhone === 'string' ? data.customerPhone.trim() : data.customerPhone,
        customerSource: typeof data.customerSource === 'string' ? data.customerSource.trim() : data.customerSource,
        employee: typeof data.employee === 'string' ? data.employee.trim() : data.employee,
        signingTime: this.formatDate(data.signingTime),
        projectName: typeof data.projectName === 'string' ? data.projectName.trim() : data.projectName,
        address: typeof data.address === 'string' ? data.address.trim() : data.address,
        groupTime: data.groupTime ? this.formatDate(data.groupTime) : '',
        province: typeof data.province === 'string' ? data.province.trim() : data.province,
        city: typeof data.city === 'string' ? data.city.trim() : data.city,
        district: typeof data.district === 'string' ? data.district.trim() : data.district || '',
        lng: String(typeof data.lng === 'string' ? data.lng.trim() : data.lng),
        lat: String(typeof data.lat === 'string' ? data.lat.trim() : data.lat),
        area: String(typeof data.area === 'string' ? data.area.trim() : data.area),
        signingType: typeof data.signingType === 'string' ? data.signingType.trim() : data.signingType,
        dudao: typeof data.dudao === 'string' ? data.dudao.trim() : data.dudao || '',
        type: typeof data.type === 'string' ? data.type.trim() : data.type,
        feeType: typeof data.feeType === 'string' ? data.feeType.trim() : data.feeType,
        payType: typeof data.payType === 'string' ? data.payType.trim() : data.payType,
        payAccount: typeof data.payAccount === 'string' ? data.payAccount.trim() : data.payAccount,
        totalAmount: parseFloat(data.totalAmount),
        payDate: this.formatDate(data.payDate)
      }
    },

    async handleUpload () {
      try {
        const confirmResult = await this.$confirm(
          `确定要上传 ${this.mergedData.length} 条数据吗？`,
          '确认上传',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        if (confirmResult !== 'confirm') return
        this.resetUploadStatus()
        this.uploadStatus.uploading = true
        this.uploadStatus.total = this.mergedData.length
        this.uploadStatus.results = this.mergedData.map((data, index) => ({
          index: index + 1,
          customerName: data.customerName,
          status: 'pending',
          message: '',
          details: [],
          error: '',
          timestamp: ''
        }))
        await this.uploadDataSequentially()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('上传失败: ' + error.message)
        }
      }
    },

    async uploadDataSequentially () {
      for (let i = this.uploadStatus.currentIndex; i < this.mergedData.length; i++) {
        if (this.uploadStatus.cancelled) {
          this.$message.info('上传已取消')
          break
        }
        while (this.uploadStatus.paused) {
          await new Promise(resolve => setTimeout(resolve, 100))
          if (this.uploadStatus.cancelled) {
            this.$message.info('上传已取消')
            return
          }
        }
        await this.uploadSingleData(i)
        this.uploadStatus.currentIndex = i + 1
      }
      this.uploadStatus.uploading = false
      if (!this.uploadStatus.cancelled) {
        this.$message.success(`上传完成！成功 ${this.uploadStatus.successCount} 条，失败 ${this.uploadStatus.failCount} 条`)
      }
    },

    async uploadSingleData (index) {
      const result = this.uploadStatus.results[index]
      result.status = 'uploading'
      result.timestamp = new Date().toLocaleString()
      try {
        const requestData = this.prepareUploadData(this.mergedData[index])
        const response = await this.$http.post('import/customer', requestData)
        if (response.data.meta.status === 200) {
          result.status = 'success'
          result.message = '导入成功'
          result.details = response.data.data || []
          this.uploadStatus.successCount++
        } else {
          throw new Error(response.data.meta.msg || '上传失败')
        }
      } catch (error) {
        result.status = 'fail'
        result.message = '导入失败'
        result.error = error.message || error.response?.data?.meta?.msg || '未知错误'
        this.uploadStatus.failCount++
      }
    },

    resetUploadStatus () {
      this.uploadStatus = {
        uploading: false,
        paused: false,
        cancelled: false,
        currentIndex: 0,
        total: 0,
        successCount: 0,
        failCount: 0,
        results: []
      }
    },

    handlePause () {
      this.uploadStatus.paused = true
      this.$message.info('上传已暂停')
    },

    handleResume () {
      this.uploadStatus.paused = false
      this.$message.info('继续上传')
    },

    handleCancel () {
      this.$confirm('确定要取消上传吗？已上传的数据将保留。', '确认取消', {
        confirmButtonText: '确定',
        cancelButtonText: '继续上传',
        type: 'warning'
      }).then(() => {
        this.uploadStatus.cancelled = true
        this.uploadStatus.uploading = false
        this.$message.info('上传已取消')
      }).catch(() => {})
    },

    async handleRetry (index) {
      const realIndex = index - 1
      const result = this.uploadStatus.results[realIndex]
      if (result.status !== 'fail') return
      result.status = 'uploading'
      result.timestamp = new Date().toLocaleString()
      try {
        const requestData = this.prepareUploadData(this.mergedData[realIndex])
        const response = await this.$http.post('import/customer', requestData)
        if (response.data.meta.status === 200) {
          result.status = 'success'
          result.message = '导入成功'
          result.details = response.data.data || []
          result.error = ''
          this.uploadStatus.successCount++
          this.uploadStatus.failCount--
          this.$message.success(`第 ${index} 条数据重试成功`)
        } else {
          throw new Error(response.data.meta.msg || '上传失败')
        }
      } catch (error) {
        result.status = 'fail'
        result.message = '导入失败'
        result.error = error.message || error.response?.data?.meta?.msg || '未知错误'
        this.$message.error(`第 ${index} 条数据重试失败: ${result.error}`)
      }
    },

    async handleRetryAllFailed () {
      const failedItems = this.uploadStatus.results.filter(r => r.status === 'fail')
      if (failedItems.length === 0) {
        this.$message.info('没有需要重试的失败项')
        return
      }
      try {
        await this.$confirm(
          `确定要重试 ${failedItems.length} 条失败数据吗？`,
          '确认重试',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        for (const item of failedItems) {
          await this.handleRetry(item.index)
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('重试失败: ' + error.message)
        }
      }
    },

    getStatusType (status) {
      const typeMap = {
        pending: 'info',
        uploading: 'warning',
        success: 'success',
        fail: 'danger'
      }
      return typeMap[status] || 'info'
    },

    getStatusText (status) {
      const textMap = {
        pending: '待上传',
        uploading: '上传中',
        success: '成功',
        fail: '失败'
      }
      return textMap[status] || status
    }
  }
}
</script>

<style lang="less" scoped>
.flexContainer {
  display: flex;
  align-items: center;
}

.cardHeader {
  font-size: 18px;
  font-weight: bold;
}

.upload-section {
  margin-bottom: 30px;
}

.upload-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 20px;
  height: 100%;

  h4 {
    margin: 0 0 15px 0;
    font-size: 16px;
    color: #303133;
  }
}

.upload-demo {
  width: 100%;
}

.button-group {
  margin-top: 30px;
  text-align: center;
}

.validation-section {
  margin-top: 30px;
}

.error-section {
  margin-top: 20px;
}

.data-section {
  margin-top: 30px;
}

.upload-button-group {
  margin-top: 20px;
  text-align: center;

  .el-button {
    margin: 0 5px;
  }
}

.upload-progress-section {
  margin-top: 30px;

  .progress-text {
    font-size: 14px;
    color: #606266;
  }
}

.upload-results-section {
  margin-top: 30px;

  .el-tag {
    font-size: 12px;
  }
}

.upload-summary-section {
  margin-top: 20px;
}
</style>
